import { createAsyncThunk } from '@reduxjs/toolkit'
import api from '../../../utils/callApi'
import { dedupeInFlight } from '../../requestDedupe'
import { setUserCharacters } from '../game/reducer'
import { filterEphemeralPvpRosterCharacters } from '../../../utils/rosterCharacters'

/** Stable cache key for character list fetches (must match dedupe key segments). */
export function getCharactersFetchCacheKey(args: {
  walletAddress: string
  characterType?: 'hero' | 'worker'
  nativeCastle?: number
  castleId?: number
}): string {
  return `characters:fetch:${args.walletAddress}:${args.characterType ?? ''}:${String(args.nativeCastle ?? '')}:${String(args.castleId ?? '')}`
}

/**
 * Fetches that represent the main-land / wallet-global owned roster (not castle-scoped API subsets).
 * Those responses are mirrored into `game.userCharacters` so land/PvP-style flows reuse bootstrap data.
 */
export function isOwnedRosterMirrorToGameSlice(args: {
  characterType?: 'hero' | 'worker'
  nativeCastle?: number
  castleId?: number
}): boolean {
  if (args.characterType) return false
  if (args.castleId != null && args.castleId > 0) return false
  const nc = args.nativeCastle
  if (nc != null && nc !== 0) return false
  return true
}

// ✨ Inventory Item Interface
export interface InventoryItem {
  _id: string
  user: string
  item: string
  stock: number
  rarity: 'common' | 'rare' | 'epic'
  createdAt: Date
  updatedAt: Date
}

// ✨ Fetch Items Action
export const fetchItems = createAsyncThunk<
  InventoryItem[],
  { walletAddress: string },
  { rejectValue: string }
>(
  'inventory/fetchItems',
  async ({ walletAddress }, { rejectWithValue }) => {
    try {
      const response = await api('inventory/fetch-items', 'post', {
        walletAddress
      })

      if (response.success) {
        return response.items || []
      } else {
        return rejectWithValue(response.message || 'Failed to fetch items')
      }
    } catch (error) {
      return rejectWithValue('Network error while fetching items')
    }
  }
)

export const fetchCharacters = createAsyncThunk<
  InventoryItem[],
  { walletAddress: string; characterType?: 'hero' | 'worker'; nativeCastle?: number; castleId?: number },
  { rejectValue: string }
>(
  'inventory/fetchCharacters',
  async ({ walletAddress, characterType, nativeCastle, castleId }, { rejectWithValue, dispatch }) => {
    try {
      const dedupeKey = getCharactersFetchCacheKey({
        walletAddress,
        characterType,
        nativeCastle,
        castleId,
      })
      const response = await dedupeInFlight(dedupeKey, () =>
        api('characters/fetch-characters', 'post', {
          walletAddress,
          characterType,
          nativeCastle, // 0 = main land, 1-4 = castle 1-4 (for filtering characters by their nativeCastle)
          castleId // 1-4 = specific castle (for fetching building assignments from CastleBuilding)
        })
      )

      if (response.success) {
        const data = filterEphemeralPvpRosterCharacters(response.data || [])
        if (isOwnedRosterMirrorToGameSlice({ characterType, nativeCastle, castleId })) {
          dispatch(setUserCharacters(data))
        }
        return data
      } else {
        return rejectWithValue(response.message || 'Failed to fetch characters')
      }
    } catch (error) {
      return rejectWithValue('Network error while fetching characters')
    }
  }
)

// ✨ Claim Chest Action
export const claimChest = createAsyncThunk<
  { success: boolean; message: string; earnedItems?: any[] },
  { walletAddress: string },
  { rejectValue: string }
>(
  'inventory/claimChest',
  async ({ walletAddress }, { rejectWithValue }) => {
    try {
      const response = await api('inventory/claim-chest', 'post', {
        walletAddress
      })
      
      if (response.success) {
        return response
      } else {
        return rejectWithValue(response.message || 'Failed to claim chest')
      }
    } catch (error) {
      return rejectWithValue('Network error while claiming chest')
    }
  }
)

// ✨ Set Item to Character Action
export const setItemToCharacter = createAsyncThunk<
  { success: boolean; message: string },
  { 
    walletAddress: string
    characterId: string
    item: string,
    rarity: 'common' | 'rare' | 'epic',
    order: number
  },
  { rejectValue: string }
>(
  'inventory/setItemToCharacter',
  async ({ walletAddress, characterId, item, rarity, order }, { rejectWithValue }) => {
    try {
      const response = await api('inventory/set-item', 'post', {
        walletAddress,
        characterId,
        item,
        rarity,
        order
      })
      
      if (response.success) {
        return response
      } else {
        return rejectWithValue(response.message || 'Failed to set item to character')
      }
    } catch (error) {
      return rejectWithValue('Network error while setting item')
    }
  }
)
